import {CheckboxSwitch} from "../../core/components/checkbox/checkboxSwitch";
import {Localizer} from "../../infrastructure/localization/localizer";
import {Dropdown, DropdownOption} from "../../core/components/dropdown/dropdown";
import React, {useEffect, useMemo, useState} from "react";
import {MultiValue} from "react-select";
import {toDropdownOptions} from "../../core/sharedmodels/forSelectModel";
import useStamdataApi from "../../core/hooks/useStamdataApi";
import {LoadingCircle} from "./loadingCircle";
import {DashboardConstants} from "./dashboardConstants";
import useScreenResolution from "../../core/hooks/useScreenResolution";
import classNames from "classnames";
import {DashboardFilterModel} from "./models/DashboardFilter";
import useLogbogApi from "../../core/hooks/useLogbogApi";
import useUser from "../../core/hooks/useUser";
import DynamicButton from "../../core/components/button/dynamicButton";
import {EmblaIcons} from "../../core/emblaIcons";
import {UserSendEmailResponseModel} from "../../services/api/stamdataUser/UserSendEmailResponseModel";
import {SlideIn} from "../../core/components/slideIn/slideIn";
import {EmailSlideInModalMultipleUsers} from "../../core/components/mail/emailSlideInModalMultipleUsers";
import {EmailConstants} from "../../core/components/mail/emailConstants";
import Tooltip from "../../core/components/tooltips/Tooltip";
import {RolleTitelEnum} from "../../infrastructure/enums/rolleTitelEnum";
import {GetUddannelsestypeTranslation, UddannelseTypeEnum} from "../../core/sharedmodels/stilling/uddannelseTypeEnum";

type DashboardFiltersProps = {
    filter: DashboardFilterModel;
    setFilter: React.Dispatch<React.SetStateAction<DashboardFilterModel>>
    initialFetchRef: React.MutableRefObject<boolean>;
    maxEmailAmountExceeded?: boolean;
}

export const DashboardFilters = ({filter, setFilter, initialFetchRef, maxEmailAmountExceeded = false}: DashboardFiltersProps) => {
    const {isMobile} = useScreenResolution();
    const {stamdataSpecialeApi, stamdataAfdelingApi, stamdataLaegeinstitutionApi} = useStamdataApi();
    const {logbogExportApi, dashboardApi} = useLogbogApi();
    const {currentUser} = useUser();
    const isUko = currentUser.hasRolleTitel(RolleTitelEnum.Uko);

    const [showEmailSlideIn, setShowEmailSlideIn] = useState(false);
    const [filteredUserModels, setFilteredUserModels] = useState<UserSendEmailResponseModel[]>([]);
    const emailSlideInModalId = "dashboard-filters-send-email-modal";

    const [specialeOptions, setSpecialeOptions] = useState<DropdownOption<string>[]>([]);
    const [uddannelsesstederOptions, setUddannelsesstederOptions] = useState<DropdownOption<string>[]>([]);

    useEffect(() => {
        const fetchSpecialeOptions = async () => {
            return await stamdataSpecialeApi.getSpecialerSelect()
        }
        const fetchUddannelsesstederOptions = async () => {
            const afdelinger = await stamdataAfdelingApi.getAfdelingerSelect();
            const praksisser = await stamdataLaegeinstitutionApi.getPraksisSelect();
            return afdelinger.concat(praksisser).sortBy(x => x.text);
        }
        fetchSpecialeOptions().then((response) => {
            setSpecialeOptions(toDropdownOptions(response));
        });
        fetchUddannelsesstederOptions().then((response) => {
            setUddannelsesstederOptions(toDropdownOptions(response));
        });
    }, [stamdataAfdelingApi, stamdataLaegeinstitutionApi, stamdataSpecialeApi])

    const handleSpecialerChange = (value: MultiValue<DropdownOption<string>>) => {
        const specialer = value.map(x => x.value);
        setFilter((prev) => ({...prev, specialeIds: specialer}))
    };

    const handleUddannelsesstederChange = (value: MultiValue<DropdownOption<string>>) => {
        const uddannelsessteder = value.map(x => x.value);
        setFilter((prev) => ({...prev, uddannelsesstedIds: uddannelsessteder}))
    };

    const handleUddannelsestrinChange = (value: MultiValue<DropdownOption<number>>) => {
        const uddannelsestrin = value.map(x => x.value);
        setFilter((prev) => ({...prev, uddannelsestrinIds: uddannelsestrin}))
    };

    const uddannelsestrinOptions = useMemo<DropdownOption<number>[]>(() => [
        {value: UddannelseTypeEnum.KBU, label: GetUddannelsestypeTranslation(UddannelseTypeEnum.KBU)},
        {value: UddannelseTypeEnum.Introduktion, label: GetUddannelsestypeTranslation(UddannelseTypeEnum.Introduktion)},
        {value: UddannelseTypeEnum.Hoveduddannelse, label: GetUddannelsestypeTranslation(UddannelseTypeEnum.Hoveduddannelse)}
    ], []);

    const onIncludeOrlovChange = (value: boolean) => {
        setFilter((prev) => ({...prev, includeOrlov: value}))
    };

    const handleExportToExcel = async () => {
        await logbogExportApi.getDashboardForloebExcel(filter);
    };

    const handleOnSendEmailClick = async () => {
        const users = await dashboardApi.getDashboardUsersForEmail(filter);
        setFilteredUserModels(users);
        setShowEmailSlideIn(true);
    };

    const closeSlideIn = () => {
        setShowEmailSlideIn(false);
    };

    return (
        <div className={"flex-container-column flex-align-items-end"}>
            {!isUko &&
                <div className={"flex-container-row margin-bottom-s margin-right-m"}>
                    <DynamicButton additionalClasses={'margin-right-s'} useFlex onClick={handleExportToExcel}>
                        <DynamicButton.TextIconItem iconName={EmblaIcons.Incoming} text={Localizer.GetExcel()}/>
                    </DynamicButton>
                    {currentUser.canSendMultipleEmails() &&
                        <Tooltip show={maxEmailAmountExceeded} title={Localizer.validate_emailBulkAllowanceMessage(EmailConstants.maxAmount)}>
                            <DynamicButton useFlex disabled={maxEmailAmountExceeded} onClick={handleOnSendEmailClick}>
                                <DynamicButton.TextIconItem iconName={EmblaIcons.Email} text={Localizer.createMail_SendMail()}/>
                            </DynamicButton>
                        </Tooltip>
                    }
                </div>
            }
            <div className={"flex-container-row wrap margin-bottom-s align-content-center"}>
            {!initialFetchRef.current &&
                <LoadingCircle duration={DashboardConstants.FilterTimer}
                               filter={filter}
                               optionalText={Localizer.dashboard_applyingFilter()}
                               additionalClasses={isMobile ? "margin-top-s margin-left-s" : ""}/>
            }
            <div className={classNames("flex-container-row align-items-center margin-right-m", isMobile && "margin-s")}>
                <CheckboxSwitch
                    id={"include-orlov-dashboard"}
                    reverseTextAndSwitch
                    label={Localizer.dashboard_includeUddannelseslaegerOnOrlov()}
                    onChange={(e) => onIncludeOrlovChange(e.target.checked)}
                    value={filter?.includeOrlov}
                />
            </div>
            <Dropdown
                className={classNames("margin-right-m", isMobile && "margin-left-s")}
                placeholder={Localizer.global_alleSpecialer()}
                options={specialeOptions}
                closeMenuOnSelect={false}
                minWidth={"300px"}
                maxWidth={"300px"}
                renderMultiValuesAsTextAppendTextOverride={Localizer.global_specialer().toLowerCase()}
                renderMultiValuesAsText
                onChange={(newValue) => handleSpecialerChange(newValue)}
                value={specialeOptions?.filter(x => filter?.specialeIds?.includes(x.value))}
                isClearable
                isMulti
            />
            <Dropdown
                className={classNames("margin-right-m", isMobile && "margin-s")}
                closeMenuOnSelect={false}
                minWidth={"300px"}
                maxWidth={"300px"}
                renderMultiValuesAsTextAppendTextOverride={Localizer.uddannelsessteder().toLowerCase()}
                placeholder={Localizer.AlleUddannelsessteder()}
                renderMultiValuesAsText
                options={uddannelsesstederOptions}
                onChange={(newValue) => handleUddannelsesstederChange(newValue)}
                value={uddannelsesstederOptions?.filter(x => filter?.uddannelsesstedIds?.includes(x.value))}
                isClearable
                isMulti
            />
            <Dropdown
                className={classNames("margin-right-m", isMobile && "margin-s")}
                closeMenuOnSelect={false}
                minWidth={"220px"}
                maxWidth={"220px"}
                renderMultiValuesAsTextAppendTextOverride={Localizer.global_uddannelsestrin().toLowerCase()}
                placeholder={Localizer.global_uddannelsestrin()}
                renderMultiValuesAsText
                options={uddannelsestrinOptions}
                onChange={(newValue) => handleUddannelsestrinChange(newValue)}
                value={uddannelsestrinOptions?.filter(x => filter?.uddannelsestrinIds?.includes(x.value))}
                isClearable
                isMulti
            />
            </div>
            {showEmailSlideIn &&
                <SlideIn
                    id={emailSlideInModalId}
                    title={Localizer.sendmail()}
                    actionFinishedCallback={closeSlideIn}
                    actionOnCloseCallback={closeSlideIn}
                    bodyContent={
                        <EmailSlideInModalMultipleUsers
                            users={filteredUserModels}
                            actionFinishedCallback={closeSlideIn}
                        />
                    }
                    defaultOpen={true}
                />
            }
        </div>
    )
}
